import React, { useState, useEffect } from 'react';
import { useSpring, useTrail, animated as a, config } from 'react-spring';
import playerStyles from './player.module.css';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import SkipPreviousIcon from '@mui/icons-material/SkipPrevious';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import CloseIcon from '@mui/icons-material/Close';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';

function Player({ podcast, onClose }) {
  const [isPlaying, setIsPlaying] = useState(true);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [volume, setVolume] = useState(70);

  const currentEpisode = podcast?.episodes?.[0] || {};

  // Smooth progress bar animation
  const progressSpring = useSpring({
    width: `${progress}%`,
    config: { tension: 280, friction: 60 }
  });

  // Volume bar animation
  const volumeSpring = useSpring({
    width: `${volume}%`,
    config: config.gentle
  });

  // Image entrance animation
  const imageSpring = useSpring({
    from: {
      opacity: 0,
      transform: 'translateY(20px)',
    },
    to: {
      opacity: 1,
      transform: 'translateY(0px)',
    },
    config: { tension: 120, friction: 14 }
  });

  // Staggered text animations
  const textElements = [currentEpisode.title, podcast.title];
  const textTrail = useTrail(textElements.length, {
    from: { opacity: 0, transform: 'translateY(20px)' },
    to: { opacity: 1, transform: 'translateY(0px)' },
    config: { tension: 200, friction: 20 },
    delay: 200
  });

  // Play button animation
  const [playBtnHover, setPlayBtnHover] = useState(false);
  const playBtnSpring = useSpring({
    boxShadow: playBtnHover
      ? '0 8px 20px rgba(0, 0, 0, 0.2)'
      : '0 4px 12px rgba(0, 0, 0, 0.1)',
    config: { tension: 300, friction: 10 }
  });

  // Control buttons trail animation
  const controlButtons = [0, 1, 2];
  const controlTrail = useTrail(controlButtons.length, {
    from: { opacity: 0, transform: 'translateY(10px)' },
    to: { opacity: 1, transform: 'translateY(0px)' },
    config: { tension: 200, friction: 20 },
    delay: 300
  });

  useEffect(() => {
    if (isPlaying) {
      const interval = setInterval(() => {
        setCurrentTime(prev => {
          const newTime = prev + 1;
          const totalSeconds = parseDuration(currentEpisode.duration);
          setProgress((newTime / totalSeconds) * 100);
          if (newTime >= totalSeconds) {
            setIsPlaying(false);
            return totalSeconds;
          }
          return newTime;
        });
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [isPlaying, currentEpisode.duration]);

  const parseDuration = (duration) => {
    const [minutes, seconds] = (duration || '0:00').split(':').map(Number);
    return minutes * 60 + seconds;
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const totalSeconds = parseDuration(currentEpisode.duration);

  // Close button animation
  const [closeBtnHover, setCloseBtnHover] = useState(false);
  const closeBtnSpring = useSpring({
    transform: closeBtnHover ? 'rotate(90deg)' : 'rotate(0deg)',
    config: { tension: 300, friction: 10 }
  });

  // Header title animation
  const headerSpring = useSpring({
    from: { opacity: 0, transform: 'translateY(-10px)' },
    to: { opacity: 1, transform: 'translateY(0px)' },
    config: { tension: 200, friction: 20 },
    delay: 100
  });

  return (
    <div className={playerStyles.player}>
      <div className={playerStyles.header}>
        <a.h2 className={playerStyles.headerTitle} style={headerSpring}>
          Now Playing
        </a.h2>
        <a.button
          className={playerStyles.closeBtn}
          onClick={onClose}
          style={closeBtnSpring}
          onMouseEnter={() => setCloseBtnHover(true)}
          onMouseLeave={() => setCloseBtnHover(false)}
        >
          <CloseIcon fontSize="small" />
        </a.button>
      </div>

      <div className={playerStyles.content}>
        <div className={playerStyles.hero}>
          <a.img
            className={playerStyles.image}
            src={podcast.image}
            alt={podcast.title}
            style={imageSpring}
          />
          <a.h3 className={playerStyles.epTitle} style={textTrail[0]}>
            {currentEpisode.title || 'Episode'}
          </a.h3>
          <a.p className={playerStyles.showName} style={textTrail[1]}>
            {podcast.title}
          </a.p>
        </div>

        <div className={playerStyles.progressContainer}>
          <div className={playerStyles.bar}>
            <a.div className={playerStyles.progress} style={progressSpring} />
          </div>
          <div className={playerStyles.time}>
            <span>{formatTime(currentTime)}</span>
            <span>{currentEpisode.duration || '0:00'}</span>
          </div>
        </div>

        <div className={playerStyles.controls}>
          <a.button className={playerStyles.controlBtn} style={controlTrail[0]}>
            <SkipPreviousIcon />
          </a.button>
          <a.button
            className={playerStyles.playBtn}
            onClick={() => setIsPlaying(!isPlaying)}
            style={{ ...controlTrail[1], ...playBtnSpring }}
            onMouseEnter={() => setPlayBtnHover(true)}
            onMouseLeave={() => setPlayBtnHover(false)}
          >
            {isPlaying ? <PauseIcon sx={{ fontSize: 36 }} /> : <PlayArrowIcon sx={{ fontSize: 36 }} />}
          </a.button>
          <a.button className={playerStyles.controlBtn} style={controlTrail[2]}>
            <SkipNextIcon />
          </a.button>
        </div>

        <div className={playerStyles.volumeContainer}>
          <VolumeUpIcon fontSize="small" />
          <div
            className={playerStyles.volumeBar}
            onClick={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              const x = e.clientX - rect.left;
              const newVolume = Math.round((x / rect.width) * 100);
              setVolume(Math.max(0, Math.min(100, newVolume)));
            }}
          >
            <a.div className={playerStyles.volumeProgress} style={volumeSpring} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default Player;